import mqtt from 'mqtt';
import EnvUtils from '../EnvUtils/index.js';
import { isEmpty, isFunction, isNullOrUnDef } from '../is.js';
import { awaitTo, replaceTemplate } from '../utils.js';
import { getMqttConfig } from '@/api/mqtt.js';

const browserProtocol = location.protocol.indexOf('https') !== -1 ? 'wss:' : 'ws:';

let [prefix, ip, port, protocol, username, password] = EnvUtils.getEnvs([
  'MQTT_PREFIX',
  'MQTT_IP',
  'MQTT_PORT',
  'MQTT_PROTOCOL',
  'MQTT_USERNAME',
  'MQTT_PASSWORD'
]);

const _port = port;

protocol = protocol === 'dynamic' || isEmpty(protocol) ? browserProtocol : protocol;

export const envPrefix = prefix;

const opt = { ip, port, protocol, username, password };

let url = `${opt.protocol}//${opt.ip}:${opt.port}`;
const originalUrl = url;

const config = {
  default: {
    protocolId: 'MQTT',
    protocolVersion: 4,
    username: opt.username,
    password: opt.password
  }
};

/** @type {Map<string, import('mqtt').MqttClient>} */
const map = new Map();

/**
 * mqtt 注册
 * @param {string} key
 * @returns {Promise<import('mqtt').Client>}
 */
export async function register(key = 'default') {
  let client = map.get(key);

  const _config = config[key];

  if (!_config.clientId) {
    if (userId) {
      _config.clientId = `GID_308PRD@@@WEB_${userId}${window.mqttSuffix || ''}`;

      const [err, res] = await awaitTo(getMqttConfig(_config.clientId));

      if (err) {
        console.error(err);
        return;
      }

      let { username, password, mqttUrl, port } = res.data || {};

      if (username && password) {
        _config.username = username;
        _config.password = password;

        if (EnvUtils.is('development')) {
          port = _port;
        }

        url = `${opt.protocol}//${mqttUrl}:${port}`;
      }
    }
  }

  if (isNullOrUnDef(client)) {
    client = mqtt.connect(url, _config);
    map.set(key, client);
  }
  return client;
}

function getClient(key = 'default') {
  return map.get(key) || null;
}

/**
 * mqtt 订阅
 * @typedef {() => void} Stop
 * @param {string} topic
 * @param {(event: string, message: Record<string, any>) => void} callback
 * @return {Stop} 停止监听和订阅
 */
export function subscribe(topic, callback) {
  topic = getTopic(topic);

  const client = getClient();

  if (!client) {
    console.warn(`[MQTT] subscribe failed: no client for topic "${topic}"`);
    return () => {};
  }

  client.subscribe(topic, { qos: 1 });

  if (!isFunction(callback)) {
    return () => {
      unsubscribe(topic);
    };
  }

  const _cb = (event, message) => {
    if (event !== topic) return;

    try {
      const msg = message.toString();
      callback(event, JSON.parse(msg));
    } catch (e) {
      console.warn(`[MQTT] parse message failed for topic "${topic}":`, e);
    }
  };

  client.on('message', _cb);

  return () => {
    client.off('message', _cb);
    unsubscribe(topic);
  };
}

/**
 * mqtt 取消订阅
 * @param {string} topic
 */
export function unsubscribe(topic) {
  const client = getClient();
  if (client) {
    client.unsubscribe(topic);
  }
}

let userId = null;

/**
 * mqtt设置用户id
 * @param {string} _userId
 */
export function setUserId(_userId) {
  userId = _userId;
}

/**
 * 获取topic
 * @example getTopic(`{prefix}/{userId}/xxx`)
 * @param {string} temp
 * @returns {string}
 */
export function getTopic(temp) {
  return replaceTemplate(temp, { prefix: envPrefix, userId });
}

/**
 * 事件映射表 (双向)
 */
let _eventMapCache = null;

function getEventMap() {
  if (_eventMapCache) return _eventMapCache;

  const p = `${envPrefix}/${userId}`;

  const pairs = [
    [`${p}/monitor/detail/`, '/socket/detail'],
    [`${p}/basic/`, '/socket/basic'],
    [`${p}/monitor/param/`, '/socket/param'],
    [`${p}/devicemap/detail/`, '/socket/devicemap/detail'],
    [`${p}/devicemap/replay/detail/`, '/socket/devicemap/replay/detail'],
    [`${p}/detect/detail/`, '/socket/detect/detail'],
    [`${p}/alarm/`, '/socket/alarm'],
    [`${p}/alarm/dispose/finish/`, '/socket/alarm/dispose/finish']
  ];

  _eventMapCache = pairs.reduce((map, e) => {
    map[(map[e[0]] = e[1])] = e[0];
    return map;
  }, {});

  return _eventMapCache;
}

/**
 * 获取事件key (双向映射)
 * @param {string} event
 * @returns {string}
 */
export function getEventKey(event) {
  return getEventMap()[event];
}

export function resetMqtt() {
  map.forEach(client => client.end());
  map.clear();
  Object.values(config).forEach(e => {
    e.clientId = '';
  });
  _eventMapCache = null;
  url = originalUrl;
  setUserId(null);
}
